import { getLoggedUser } from "@/utils/sessionUtils";

// O bundler substitui `process.env.BUN_PUBLIC_API_BASE_URL` quando a variável existe;
// em dev sem a variável a expressão chega crua ao browser (process não existe).
function readApiBaseUrl(): string | undefined {
	try {
		return process.env.BUN_PUBLIC_API_BASE_URL;
	} catch {
		return undefined;
	}
}

export const API_BASE_URL = readApiBaseUrl() ?? "http://localhost:8080";

// Module-level token ref kept in sync by AuthContext.
// apiFetch reads from here instead of localStorage so the access token
// never needs to be persisted to disk.
export const tokenRef: { current: string | null } = {
	current: null,
};

// Injected by AuthContext on mount so apiFetch can trigger a logout
// without importing AuthContext (which would create a circular dep).
export const authCallbacks: {
	refresh: (() => Promise<string>) | null;
	logout: (() => void) | null;
} = { refresh: null, logout: null };

type ApiOptions = RequestInit & {
	clientAware?: boolean;
	skipAuth?: boolean;
};

async function executeRequest<T>(
	path: string,
	options: ApiOptions,
): Promise<T> {
	const headers = new Headers(options.headers);

	headers.set("Content-Type", "application/json");

	if (!options.skipAuth && tokenRef.current) {
		headers.set("Authorization", `Bearer ${tokenRef.current}`);
	}

	if (options.clientAware) {
		const sessionUser = getLoggedUser();
		if (sessionUser?.loggedClient) {
			headers.set("Client-Token", sessionUser.loggedClient.id);
		}
	}

	let response: Response;
	try {
		response = await fetch(`${API_BASE_URL}${path}`, {
			...options,
			headers,
		});
	} catch (err: any) {
		// Cancelamento (AbortController) segue como está; o resto é falha de
		// rede, cujo texto nativo ("Failed to fetch") vem em inglês do navegador.
		if (err?.name === "AbortError") throw err;
		throw Object.assign(
			new Error("Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente."),
			{ status: 0 },
		);
	}

	if (!response.ok) {
		let message = "";
		try {
			const body = await response.json();
			// Os handlers respondem {"error": "..."}; o login responde {"message": "..."}.
			message = body.error ?? body.message ?? "";
		} catch {}

		throw Object.assign(new Error(message || `Não foi possível concluir a operação (${response.status}).`), {
			status: response.status,
		});
	}

	if (response.status === 204) return undefined as T;
	return response.json();
}

export async function apiFetch<T>(
	path: string,
	options: ApiOptions = {},
): Promise<T> {
	try {
		return await executeRequest<T>(path, options);
	} catch (err: any) {
		if (err?.status === 401 && !options.skipAuth && authCallbacks.refresh) {
			try {
				const newToken = await authCallbacks.refresh();
				tokenRef.current = newToken;
				return await executeRequest<T>(path, options);
			} catch {
				authCallbacks.logout?.();
				throw err;
			}
		}
		throw err;
	}
}
